import { Component, OnInit, inject, signal, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportJobService, DownloadResponse } from 'src/app/core/services/reporting/report-job.service';

@Component({
  selector: 'app-reporting-download',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="download-container">
      @if (isLoading()) {
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Preparing your download...</p>
        </div>
      } @else if (error()) {
        <div class="error-state">
          <h2>Download Failed</h2>
          <p>{{ error() }}</p>
          <button class="btn btn-primary" (click)="retryDownload()">Try Again</button>
        </div>
      } @else if (downloadComplete()) {
        <div class="success-state">
          <h2>Download Complete</h2>
          <p>Your report has been downloaded successfully.</p>
          <a [routerLink]="['/customer/reporting-v2']" [queryParams]="backQueryParams()" class="btn btn-primary">Back to Reports</a>
        </div>
      }
    </div>
  `,
  styles: [`
    .download-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
      text-align: center;
    }
    .loading-state, .error-state, .success-state {
      padding: 2rem;
    }
    .spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #3498db;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .error-state h2 { color: #dc3545; }
    .success-state h2 { color: #28a745; }
    .btn {
      display: inline-block;
      padding: 0.5rem 1rem;
      margin-top: 1rem;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      text-decoration: none;
    }
    .btn-primary {
      background-color: #007bff;
      color: white;
    }
  `],
})
export class DownloadComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private reportJobService = inject(ReportJobService);
  private destroyRef = inject(DestroyRef);

  public isLoading = signal(true);
  public error = signal<string | null>(null);
  public downloadComplete = signal(false);
  public backQueryParams = signal<Record<string, string | number>>({});

  private downloadKey: string | null = null;
  private downloadInitiated = false;

  ngOnInit(): void {
    // Subscribe to query params changes to capture organizationId and account_type
    // (customer-layout adds these when primary_organization_id is present)
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      // Update back params when organizationId is available
      if (params['organizationId']) {
        const queryParams: Record<string, string | number> = {
          organizationId: params['organizationId'],
        };
        if (params['account_type']) {
          queryParams['account_type'] = params['account_type'];
        }
        this.backQueryParams.set(queryParams);
      }

      // Only initiate download once
      if (!this.downloadInitiated) {
        this.downloadKey = params['s3_path'] || null;

        if (!this.downloadKey) {
          this.isLoading.set(false);
          this.error.set('No download path provided. Please use a valid download link.');
          return;
        }

        this.downloadInitiated = true;
        this.initiateDownload();
      }
    });
  }

  public retryDownload(): void {
    if (this.downloadKey) {
      this.error.set(null);
      this.isLoading.set(true);
      this.initiateDownload();
    }
  }

  private initiateDownload(): void {
    if (!this.downloadKey) return;

    this.reportJobService.downloadReportByKey(this.downloadKey).subscribe({
      next: (response: DownloadResponse) => {
        this.triggerBrowserDownload(response.download_url);
        this.isLoading.set(false);
        this.downloadComplete.set(true);
      },
      error: (err: Error) => {
        this.isLoading.set(false);
        this.error.set(err.message || 'Failed to download report. The link may have expired.');
      },
    });
  }

  private triggerBrowserDownload(url: string): void {
    // Use a hidden iframe to trigger download while keeping user on the page
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    // Clean up iframe after download starts
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 5000);
  }
}